import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { ApiService } from './api.service';
import { SessionInfoResult } from '../models/layout/session/info/SessionInfo';
import { AppListResponse } from '../models/layout/app-list/app-list-response';
import { AppListResult } from '../models/layout/app-list/app-list-result';


@Injectable({
  providedIn: 'root'
})
export class SessionService {
    public currentSession: SessionInfo;
    private apps: AppListResult[];

    get session$(): Observable<SessionInfo> {
        return this.apiService.sessionApi
            .pipe(
                map(res => new SessionInfo(res?.cmd?.success ? res.cmd?.params : null)),
                tap(session => this.currentSession = session)
            );
    }

    get apps$() {
        if (this.apps) return of(this.apps);

        return this.session$.pipe(
            filter(session => !!session),
            switchMap(() => this.apiService.get<AppListResponse>("portal_app.list")),
            map(res => res?.cmd?.params?.result),
            tap(apps => this.apps = apps),
        )
    }

    constructor(private apiService: ApiService) {}
}

export class SessionInfo {

    get hasSuperAdmin() { return  this.data?.hasSuperAdmin }
    get hasSuperOps() { return this.data?.hasSuperOps }
    get hasOrgAdmin() { return this.data?.hasOrgAdmin }
    get SessionInfoPerms() { return this.data?.perms }
    get euiccEnabled() { return this.data?.orgProfileSettings?.enEuicc}
    get euiccAdvancedEnabled() { return this.data?.orgProfileSettings.enEuiccAdvanced ?? false; }

    get orgId() : string {
        return this.data?.orgId
    }

    get isValid() : boolean {
        return !!this.data;
    }

    constructor(private data: SessionInfoResult | undefined ){ }

    hasPermission(permission: string): boolean{
        // Auto allow for super admins
        if (this.data?.hasSuperAdmin) {
            return true;
        }

        permission = permission.toLowerCase();

        // Only superadmins have access to superadmin features
        if (permission == 'superadmin') {
            return this.hasSuperAdmin;
        }

        // Superadmins and superops have access to superops features
        if (permission == 'superops') {
            return ( this.hasSuperAdmin || this.hasSuperOps);
        }

        // Orgadmin, superadmins and superopscan have access to orgadmin features
        if (permission == 'orgadmin') {
            return this.hasOrgAdmin || this.hasSuperAdmin || this.hasSuperOps;
        }

        // Explicit check for support permission
        if (permission == 'support') {
            return this.data.support;
        }

        // Allow OrgAdmin, SuperOps, and SuperAdmin to execute all other actions/services
        if (this.hasOrgAdmin || this.hasSuperOps || this.hasSuperAdmin) {
            return true;
        }

        if (!this.SessionInfoPerms) {
            return false;
        }

        // check to see if the user has access to the action
        if (this.SessionInfoPerms[permission]) {
            return this.SessionInfoPerms[permission];
        }

        if(!permission.includes('.')){
            return false;
        }

        return this.SessionInfoPerms[permission.split('.')[0]];

    }
}
